// Device registration
export interface DeviceRegistration {
  deviceId: string;
  deviceSecret: string;
  brandId: string;
  storeId: string;
  zoneId?: string;
}

// Manifest types (from API - matching @govision/shared Manifest type)
export interface ManifestPlaylistItem {
  id: string;
  assetId?: string;    // Optional — one of assetId, layoutId, or designId
  layoutId?: string;   // Layout items reference a screen layout
  designId?: string;   // Design items reference a dynamic design
  duration: number; // seconds
  fitMode: 'FIT' | 'FILL';
  weight?: number;
}

export interface ManifestPlaylist {
  id: string;
  name: string;
  mode: 'sequential' | 'shuffle' | 'weighted';
  items: ManifestPlaylistItem[];
}

export interface ManifestScheduleRule {
  id?: string;
  playlistId: string;
  priority: number;
  isInterrupt?: boolean;
  rrule?: string;
  startTime: string;
  endTime: string;
  timezone: string;
  days?: number[]; // 0=Sunday, 1=Monday, ..., 6=Saturday - computed from rrule if not provided
}

export interface ManifestSchedule {
  activePlaylistId: string;
  fallbackPlaylistId?: string; // Fallback playlist when primary fails or offline
  rules: ManifestScheduleRule[];
}

export interface ManifestAsset {
  id: string;
  type: 'video' | 'image' | 'composition';
  url: string;
  sha256: string;
  size: number;
  duration?: number;
  width: number;
  height: number;
  zones?: CompositionZone[];
}

export interface CompositionZone {
  name: string;
  x: number;
  y: number;
  width: number;
  height: number;
  zIndex: number;
  contentType: string;
  assetId?: string;
  playlistId?: string;
  designId?: string;
  content?: Record<string, unknown>;
  fitMode: string;
  backgroundColor?: string;
  visible: boolean;
}

// Zone text content for text banners/scrolling text
export interface ZoneTextContent {
  content: string;
  fontSize: number;
  fontWeight: 'normal' | 'bold';
  fontStyle: 'normal' | 'italic';
  color: string;
  backgroundColor: string;
  alignment: 'left' | 'center' | 'right';
  scrolling?: boolean;
  scrollSpeed?: number;
}

// Zone content configuration
export interface ZoneContent {
  type: 'playlist' | 'asset' | 'widget' | 'text' | 'empty';
  playlistId?: string;
  assetId?: string;
  widgetType?: 'clock' | 'weather' | 'ticker' | 'rss';
  widgetConfig?: Record<string, unknown>;
  text?: ZoneTextContent;
}

// Layout zone types
export interface ManifestZone {
  id: string;
  name: string;
  x: number; // percentage 0-100
  y: number;
  width: number;
  height: number;
  zIndex: number;
  contentType: 'PLAYLIST' | 'DESIGN' | 'ASSET' | 'TEXT' | 'WIDGET' | 'EMPTY';
  playlistId?: string;
  designId?: string;
  assetId?: string;
  content?: ZoneContent; // Flexible content configuration
  fitMode: 'FIT' | 'FILL' | 'contain' | 'cover' | 'fill' | 'none';
  transition?: string;
  transitionDuration?: number;
  visible?: boolean;
  backgroundColor?: string;
  borderRadius?: number;
}

export interface ManifestLayout {
  id: string;
  name: string;
  width: number;
  height: number;
  orientation: 'LANDSCAPE' | 'PORTRAIT' | 'SQUARE';
  zones: ManifestZone[];
}

// Design layer types for canvas rendering
export type DesignLayerType = 'text' | 'image' | 'shape' | 'video' | 'widget';

export interface DesignLayerBase {
  id: string;
  type: DesignLayerType;
  x: number;
  y: number;
  width: number;
  height: number;
  rotation?: number;
  opacity?: number;
  zIndex: number;
  visible?: boolean;
}

export interface TextLayer extends DesignLayerBase {
  type: 'text';
  content: string;
  fontFamily?: string;
  fontSize?: number;
  fontWeight?: string;
  fontStyle?: string;
  color?: string;
  textAlign?: 'left' | 'center' | 'right';
  verticalAlign?: 'top' | 'middle' | 'bottom';
  lineHeight?: number;
  letterSpacing?: number;
}

export interface ImageLayer extends DesignLayerBase {
  type: 'image';
  assetId?: string;
  src?: string;
  objectFit?: 'contain' | 'cover' | 'fill';
}

export interface ShapeLayer extends DesignLayerBase {
  type: 'shape';
  shapeType: 'rectangle' | 'circle' | 'triangle' | 'line';
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  cornerRadius?: number;
}

export interface VideoLayer extends DesignLayerBase {
  type: 'video';
  assetId?: string;
  src?: string;
  loop?: boolean;
  muted?: boolean;
  autoplay?: boolean;
  objectFit?: 'contain' | 'cover' | 'fill';
  borderRadius?: number;
}

export interface WidgetLayer extends DesignLayerBase {
  type: 'widget';
  widgetType: 'clock' | 'weather' | 'qr' | 'rss' | 'social' | 'countdown';
  config?: Record<string, unknown>;
}

export type DesignLayer = TextLayer | ImageLayer | ShapeLayer | VideoLayer | WidgetLayer;

export interface ManifestDesign {
  id: string;
  name: string;
  width: number;
  height: number;
  background?: {
    type: 'color' | 'solid' | 'gradient' | 'image';
    value: string;
  };
  layers: DesignLayer[];
}

// Overlay types for promotional overlays
export interface ManifestOverlay {
  id: string;
  type: 'ticker' | 'badge' | 'lowerThird';
  enabled: boolean;
  config: {
    text: string;
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    backgroundColor?: string;
    textColor?: string;
    fontSize?: number;
    fontWeight?: 'normal' | 'bold';
    borderRadius?: number;
    padding?: number;
    scrollSpeed?: number;
    scrollDirection?: 'left' | 'right';
    position?: 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';
    animation?: 'none' | 'fadeIn' | 'slideIn' | 'pulse';
  };
}

// Video Wall types for synchronized multi-screen displays
export interface VideoWallSlicedAsset {
  sourceAssetId: string;
  row: number;
  column: number;
  url: string;
  sha256: string;
  width: number;
  height: number;
  fileSize: number;
}

// Video wall layout group (within a layout)
export interface VideoWallLayoutGroup {
  id: string;
  name: string;
  startRow: number;
  startColumn: number;
  spanRows: number;
  spanColumns: number;
  localRow?: number;
  localColumn?: number;
  playlistId?: string | null;
  slicedAssets?: VideoWallSlicedAsset[];
}

// Active layout resolved for this device
export interface VideoWallActiveLayout {
  layoutId: string;
  layoutName: string;
  group: VideoWallLayoutGroup;
}

// Layout definition with all groups
export interface VideoWallLayout {
  id: string;
  name: string;
  isDefault: boolean;
  groups: VideoWallLayoutGroup[];
}

// Schedule slot for layout transitions
export interface VideoWallScheduleSlot {
  layoutId: string;
  layoutName: string;
  name: string;
  days: number[];
  rrule?: string | null;
  startTime: string;
  endTime: string;
  timezone: string;
  priority: number;
}

export interface ManifestVideoWall {
  id: string;
  name: string;
  // This device's position in the grid
  row: number;
  column: number;
  // Grid configuration
  totalRows: number;
  totalColumns: number;
  // Resolution
  totalWidth: number;
  totalHeight: number;
  // Bezel compensation
  bezelCompensation: {
    horizontal: number;
    vertical: number;
  };
  // Calibration offsets for this device
  calibration: {
    offsetX: number;
    offsetY: number;
  };
  // Sync configuration
  syncMethod: 'software' | 'hardware_genlock' | 'hardware_sender' | 'ntp';
  hardwareController: string | null;
  controllerIp: string | null;
  controllerPort: number | null;
  // Master device ID (device at position 0,0)
  masterDeviceId: string | null;
  // Sliced assets for this device's position
  slicedAssets: VideoWallSlicedAsset[];
  // Active layout for this device
  activeLayout?: VideoWallActiveLayout | null;
  // Layout schedule for client-side evaluation
  layoutSchedule?: VideoWallScheduleSlot[];
  // All layouts with groups
  layouts?: VideoWallLayout[];
}

// Screen Group types for synchronized playback without content slicing
export interface ManifestScreenGroup {
  id: string;
  name: string;
  syncMethod: 'none' | 'master' | 'slave';
  playlistId: string | null;
  layoutId: string | null;
}

// Power Schedule Exception (Holiday) type
export interface PowerScheduleException {
  date: string; // YYYY-MM-DD format
  name: string;
  allDay: boolean;
  powerOnTime?: string | null; // HH:MM format (if allDay is false)
  powerOffTime?: string | null; // HH:MM format (if allDay is false)
  screenOff: boolean; // If true, screen stays off
}

// Power Schedule types for automatic screen on/off control
export interface ManifestPowerSchedule {
  enabled: boolean;
  powerOnTime: string | null; // HH:MM format
  powerOffTime: string | null; // HH:MM format
  // Working hours (content schedule - screen stays on but may show different content)
  workingHoursEnabled?: boolean;
  workingHoursStart?: string | null; // HH:MM format
  workingHoursEnd?: string | null; // HH:MM format
  workingDays?: number[]; // 0=Sun, 1=Mon, ..., 6=Sat
  timezone?: string;
  // Holiday exceptions - dates when normal schedule doesn't apply
  exceptions?: PowerScheduleException[];
}

export interface DeviceManifest {
  version: number;
  brandId: string;
  deviceId: string;
  deviceOrientation?: number;  // 0, 90, 180, 270
  generatedAt: string;
  expiresAt: string;
  playlists: ManifestPlaylist[];
  schedule: ManifestSchedule;
  assets: ManifestAsset[];
  signature: string;
  // Layout support (optional for backward compatibility)
  layout?: ManifestLayout;
  // Layouts referenced by playlist items (separate from device's own layout)
  layouts?: ManifestLayout[];
  designs?: ManifestDesign[];
  // Promotional overlays (optional)
  overlays?: ManifestOverlay[];
  // Video wall configuration (if device is part of a video wall)
  videoWall?: ManifestVideoWall;
  // Screen group configuration (if device is part of a synchronized group)
  screenGroup?: ManifestScreenGroup;
  // Power schedule configuration (automatic on/off times)
  powerSchedule?: ManifestPowerSchedule;
  // Custom brand fonts
  fonts?: ManifestFont[];
  // Screen brightness override (0–100). 0 = blackout, null/undefined = device default.
  screenBrightness?: number | null;
}

export interface ManifestFontVariant {
  weight: number;
  style: 'normal' | 'italic';
  url: string;
}

export interface ManifestFont {
  family: string;
  variants: ManifestFontVariant[];
}

// Legacy types for backward compatibility (used internally)
export interface PlaylistItem {
  id: string;
  assetId: string;
  assetType: 'IMAGE' | 'VIDEO' | 'HTML' | 'WIDGET';
  url: string;
  duration: number; // seconds
  transitionIn?: TransitionConfig;
  transitionOut?: TransitionConfig;
  metadata?: {
    width?: number;
    height?: number;
    mimeType?: string;
  };
}

export interface TransitionConfig {
  type: 'none' | 'fade' | 'slide' | 'zoom';
  duration: number; // ms
  direction?: 'left' | 'right' | 'up' | 'down';
}

// Player state
export interface PlayerState {
  status: 'loading' | 'playing' | 'error' | 'offline';
  currentPlaylist: string | null;
  currentItemIndex: number;
  currentAssetId: string | null;
  isOnline: boolean;
  lastHeartbeat: number;
  lastManifestUpdate: number;
  error?: string;
}

// Analytics events
export interface PlayEvent {
  assetId: string;
  playlistId: string;
  startedAt: string;
  endedAt?: string;
  durationMs?: number;
  completed: boolean;
}

export interface ViewSession {
  sessionId: string;
  startedAt: string;
  endedAt?: string;
  assetIds: string[];
}

// Storage keys
export const STORAGE_KEYS = {
  DEVICE_REGISTRATION: 'govista_device_registration',
  MANIFEST: 'govista_manifest',
  CACHED_ASSETS: 'govista_cached_assets',
  PENDING_ANALYTICS: 'govista_pending_analytics',
  PLAYER_STATE: 'govista_player_state',
} as const;

// API endpoints
export interface ApiConfig {
  baseUrl: string;
  wsUrl: string;
}

// WebSocket messages
export interface WsMessage {
  type: string;
  payload?: unknown;
  timestamp?: number;
}

export interface WsCommandMessage extends WsMessage {
  type: 'COMMAND';
  payload: {
    command: 'REFRESH' | 'REBOOT' | 'RESTART_APP' | 'SCREENSHOT';
    params?: Record<string, unknown>;
  };
}

export interface WsManifestInvalidatedMessage extends WsMessage {
  type: 'MANIFEST_INVALIDATED';
  payload: {
    version: string;
  };
}
